import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { Cloud, ScrollText, ShieldCheck, Terminal, type LucideIcon } from 'lucide-react';
import {
  STAFF_PROXY_TESTING_DEVICE_ACTIVITY_FEED,
  STAFF_TESTING_DEVICE_STAFF_ACTIONS,
} from '@staff-app/graphql-client';
import { Button, cn } from '@staff-app/ui';
import { safeDistanceToNow, safeJsonParse } from '@/lib/safe-format';
import { customerReadVars } from '../../lib/drawer-mode';
import { activityLabel } from './lib/activity-label';
import type { DeviceActivityEvent, StaffActionEntry } from './lib/types';

const LIMIT = 50;

type Filter = 'all' | 'commands' | 'staff';
const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'commands', label: 'Commands' },
  { id: 'staff', label: 'Staff actions' },
];

interface FeedResult {
  staffProxyTestingDeviceActivityFeed: string;
}
interface StaffActionsResult {
  staffTestingDeviceStaffActions: StaffActionEntry[];
}

/** One card in the feed, whichever source it came from. */
interface FeedItem {
  key: string;
  source: 'commands' | 'staff';
  /** Epoch ms for ordering (NaN when unknown — sorts last). */
  ts: number;
  at: string | null;
  Icon: LucideIcon;
  title: string;
  titleHint?: string;
  actor: string | null;
  /** Extra meta after the actor (the staff tool). */
  extra?: string | null;
  failed: boolean;
  pending?: boolean;
  detail?: string | null;
}

/** "2026-06-12 09:33:52 CST" (staff-api's display time) → epoch ms; older rows carry no ISO `at`. */
function parseCst(time: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2}) C[SD]T$/.exec(time.trim());
  if (!m) return Number.NaN;
  const offset = time.trim().endsWith('CDT') ? 5 : 6;
  const [y = 0, mo = 1, d = 1, h = 0, mi = 0, s = 0] = m.slice(1).map(Number);
  return Date.UTC(y, mo - 1, d, h + offset, mi, s);
}

function fromCommand(ev: DeviceActivityEvent): FeedItem {
  return {
    key: `c:${ev.id}`,
    source: 'commands',
    ts: Date.parse(ev.at),
    at: ev.at,
    Icon: ev.kind === 'command' ? Terminal : ev.kind === 'vendorOp' ? Cloud : ScrollText,
    title: activityLabel(ev.operationKey, ev.params),
    titleHint: ev.operationKey,
    actor: ev.actorDisplay ?? ev.actorUserId ?? null,
    failed: ev.status === 'fail',
    pending: ev.status === 'warn',
  };
}

function fromStaff(a: StaffActionEntry): FeedItem {
  const ts = a.at ? Date.parse(a.at) : parseCst(a.time);
  return {
    key: `s:${a.id}`,
    source: 'staff',
    ts,
    at: Number.isFinite(ts) ? new Date(ts).toISOString() : null,
    Icon: ShieldCheck,
    title: a.summary || a.tool,
    titleHint: a.time,
    actor: a.submittedBy && a.submittedBy !== '—' ? a.submittedBy : null,
    extra: a.tool,
    failed: a.status !== 'Success',
    detail: a.status !== 'Success' ? a.detail : null,
  };
}

/**
 * Activity tab — scio3's merged feed (All / Commands / Edits) with staff-app's own second source in
 * place of the portal "Edits": open-api `deviceActivityFeed` (commands + vendor ops) and what staff
 * did to this device through staff-app (staff_audit_log). "All" interleaves both, newest first; one
 * card per event (icon by kind · readable title · "X ago · by <actor>"). Unlike prod, command titles
 * are readable labels rather than raw `mdm.activity.*` i18n keys. Fetched only while mounted.
 * `accountId` (customer drawer only) sends both reads through staff-api's customer gate.
 */
export function ActivityTab({ deviceId, accountId }: { deviceId: string; accountId?: string }) {
  const scope = customerReadVars(accountId);
  const [filter, setFilter] = useState<Filter>('all');

  const feedQ = useQuery<FeedResult>(STAFF_PROXY_TESTING_DEVICE_ACTIVITY_FEED, {
    variables: { deviceId, limit: LIMIT, ...scope },
    fetchPolicy: 'cache-and-network',
  });
  const staffQ = useQuery<StaffActionsResult>(STAFF_TESTING_DEVICE_STAFF_ACTIONS, {
    variables: { deviceId, limit: LIMIT, ...scope },
    fetchPolicy: 'cache-and-network',
  });

  const commandItems = useMemo(
    () =>
      (
        safeJsonParse<DeviceActivityEvent[]>(feedQ.data?.staffProxyTestingDeviceActivityFeed, []) ??
        []
      ).map(fromCommand),
    [feedQ.data],
  );
  const staffItems = useMemo(
    () => (staffQ.data?.staffTestingDeviceStaffActions ?? []).map(fromStaff),
    [staffQ.data],
  );

  const items = useMemo(() => {
    const list =
      filter === 'commands'
        ? commandItems
        : filter === 'staff'
          ? staffItems
          : [...commandItems, ...staffItems];
    const key = (i: FeedItem) => (Number.isFinite(i.ts) ? i.ts : -Infinity);
    return [...list].sort((a, b) => key(b) - key(a));
  }, [filter, commandItems, staffItems]);

  const wantCommands = filter !== 'staff';
  const wantStaff = filter !== 'commands';
  const loading =
    (wantCommands && feedQ.loading && commandItems.length === 0) ||
    (wantStaff && staffQ.loading && staffItems.length === 0);
  const errors = [
    wantCommands && feedQ.error ? `Couldn't load commands. ${feedQ.error.message}` : null,
    wantStaff && staffQ.error ? `Couldn't load staff actions. ${staffQ.error.message}` : null,
  ].filter((e): e is string => !!e);

  return (
    <div className="space-y-3" data-testid="testing-activity-tab">
      <div className="flex gap-1" role="group" aria-label={'Filter activity'}>
        {FILTERS.map((f) => (
          <Button
            key={f.id}
            variant={filter === f.id ? 'secondary' : 'ghost'}
            size="sm"
            className="cursor-pointer"
            onClick={() => setFilter(f.id)}
            aria-pressed={filter === f.id}
            data-testid={`testing-activity-filter-${f.id}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {errors.map((e) => (
        <div key={e} className="rounded-md border bg-card p-3 text-sm text-muted-foreground">
          {e}
        </div>
      ))}

      {items.length === 0 ? (
        loading ? (
          <div className="text-sm text-muted-foreground">{'Loading…'}</div>
        ) : errors.length ? null : (
          <div
            className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground shadow-sm"
            data-testid="testing-activity-empty"
          >
            {filter === 'commands'
              ? 'No commands yet'
              : filter === 'staff'
                ? 'No staff actions on this device yet'
                : 'No activity yet'}
          </div>
        )
      ) : (
        <div className="space-y-2" data-testid="testing-activity-list">
          {items.map((item) => (
            <ActivityCard key={item.key} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityCard({ item }: { item: FeedItem }) {
  const { Icon } = item;
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-md border bg-card p-3 text-sm',
        item.failed && 'border-destructive/30 bg-destructive/5',
      )}
      data-testid={item.source === 'staff' ? 'testing-staff-action-row' : 'testing-activity-row'}
    >
      <span
        className={cn(
          'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
          item.failed ? 'bg-destructive/15 text-destructive' : 'bg-muted text-muted-foreground',
        )}
        aria-hidden
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium" title={item.titleHint}>
            {item.title}
          </span>
          {item.failed ? (
            <span className="text-xs font-medium text-destructive">{'failed'}</span>
          ) : item.pending ? (
            <span className="text-xs font-medium text-amber-700 dark:text-amber-500">
              {'pending'}
            </span>
          ) : null}
        </div>
        {item.failed && item.detail ? (
          <div className="mt-0.5 text-xs text-destructive">{item.detail}</div>
        ) : null}
        <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-muted-foreground">
          <span title={item.at ?? undefined}>
            {item.at ? safeDistanceToNow(item.at, { addSuffix: true }, '—') : '—'}
          </span>
          {item.actor ? (
            <span className="truncate" title={item.actor}>
              {`by ${item.actor}`}
            </span>
          ) : null}
          {item.extra ? <span>{`· ${item.extra}`}</span> : null}
        </div>
      </div>
    </div>
  );
}
